"use client";

import Image from "next/image";
import { useCallback, useState } from "react";
import { ImagePlus, Loader2, X } from "lucide-react";
import { supportApi } from "@/lib/api";
import { toAbsoluteImageUrl } from "@/lib/imageUtils";
import { AeraButton } from "@/app/components/ui/AeraButton";

type Props = {
  imageUrls: string[];
  onChange: (urls: string[]) => void;
  max?: number;
  disabled?: boolean;
  layout?: "row" | "column";
  className?: string;
};

function uploadErrorToThai(message: string): string {
  const raw = message.trim();
  if (/[\u0E00-\u0E7F]/.test(raw)) return raw;
  const m = raw.toLowerCase();
  if (/network|fetch failed|failed to fetch|timeout/.test(m)) {
    return "อัปโหลดไม่สำเร็จ กรุณาลองใหม่";
  }
  if (/too large|size|limit/.test(m)) return "ไฟล์ใหญ่เกินไป";
  if (/type|format|mime/.test(m)) return "รองรับเฉพาะไฟล์รูปภาพ (JPG, PNG, GIF, WebP)";
  return "อัปโหลดรูปไม่สำเร็จ";
}

export function SupportImageUploader({
  imageUrls,
  onChange,
  max = 5,
  disabled,
  layout = "row",
  className = "",
}: Props) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isColumn = layout === "column";

  const onPick = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files?.length || disabled) return;
      setError(null);
      setUploading(true);
      try {
        const next = [...imageUrls];
        for (const file of Array.from(files)) {
          if (next.length >= max) break;
          const res = await supportApi.uploadImage(file);
          const url = res.file?.url;
          if (url) next.push(url);
        }
        onChange(next);
      } catch (err) {
        setError(
          uploadErrorToThai(err instanceof Error ? err.message : "อัปโหลดรูปไม่สำเร็จ"),
        );
      } finally {
        setUploading(false);
        e.target.value = "";
      }
    },
    [disabled, imageUrls, max, onChange],
  );

  const thumbClass = isColumn
    ? "relative aspect-square w-full overflow-hidden rounded-lg border border-aera-charcoal/10 bg-aera-white"
    : "relative h-20 w-20 overflow-hidden rounded-xl border border-aera-charcoal/10 bg-aera-offwhite";

  return (
    <div className={`space-y-2 ${className}`.trim()}>
      <div
        className={`flex gap-2 ${isColumn ? "w-full flex-col" : "flex-wrap"}`}
      >
        {imageUrls.map((url) => {
          const abs = toAbsoluteImageUrl(url) ?? url;
          return (
            <div key={url} className={thumbClass}>
              <Image src={abs} alt="" fill className="object-cover" unoptimized />
              {!disabled ? (
                <AeraButton
                  variant="ghost"
                  size="xs"
                  type="button"
                  onClick={() => onChange(imageUrls.filter((u) => u !== url))}
                  className="absolute right-1 top-1 rounded-full bg-aera-black/55 p-0.5! text-aera-white h-auto! w-auto! min-w-0!"
                  aria-label="ลบรูป"
                >
                  <X className="h-3.5 w-3.5" />
                </AeraButton>
              ) : null}
            </div>
          );
        })}
        {imageUrls.length < max && !disabled ? (
          <label
            className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed text-center transition ${
              isColumn
                ? "aspect-square w-full gap-1 border-aera-charcoal/15 bg-aera-white px-2 py-3 text-aera-gray hover:border-aera-amber/60 hover:bg-aera-warmcream/30"
                : "h-20 w-20 border-aera-charcoal/20 bg-aera-offwhite text-aera-gray hover:border-aera-amber hover:bg-aera-warmcream/40"
            }`}
          >
            {uploading ? (
              <Loader2 className="h-5 w-5 animate-spin text-aera-amber" aria-hidden />
            ) : isColumn ? (
              <>
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-aera-warmcream">
                  <ImagePlus className="h-4 w-4 text-aera-amber" aria-hidden />
                </span>
                <span className="text-[10px] font-semibold leading-tight text-aera-charcoal">
                  เพิ่มรูป
                </span>
                <span className="text-[9px] leading-snug text-aera-gray">
                  สูงสุด {max} รูป
                </span>
              </>
            ) : (
              <>
                <ImagePlus className="h-5 w-5" aria-hidden />
                <span className="mt-0.5 text-[10px] font-medium">เพิ่มรูป</span>
              </>
            )}
            <input
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              multiple
              className="sr-only"
              onChange={onPick}
              disabled={uploading}
            />
          </label>
        ) : null}
      </div>
      {!isColumn ? (
        <p className="text-[11px] text-aera-gray">
          สูงสุด {max} รูป · JPG, PNG, GIF หรือ WebP
        </p>
      ) : null}
      {error ? <p className="text-xs text-aera-red">{error}</p> : null}
    </div>
  );
}
